import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api, { BACKEND_URL } from '../api';
import { useAuth } from '../AuthContext';
import { highlightSnippet } from '../searchHighlight';
import { useStatuses } from '../useStatuses';

function flattenDepts(nodes, depth = 0, acc = []) {
  nodes.forEach(n => {
    acc.push({ ...n, depth });
    if (n.children?.length) flattenDepts(n.children, depth + 1, acc);
  });
  return acc;
}

function DepartmentTiles({ departments, onSelect }) {
  const flat = flattenDepts(departments);
  if (flat.length === 0) {
    return (
      <div className="empty-state">
        <p>Brak działów — poproś admina o ich utworzenie w sekcji "Działy", albo przejdź od razu do wszystkich procedur.</p>
        <button className="btn btn-secondary" style={{ marginTop: 12 }} onClick={() => onSelect('__all__')}>Pokaż wszystkie procedury</button>
      </div>
    );
  }
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
        {flat.map(d => (
          <div key={d.id} className="card" style={{ cursor: 'pointer', padding: '18px 20px', transition: 'box-shadow .15s, transform .15s' }}
            onClick={() => onSelect(d.id)}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = 'var(--shadow-md)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: d.depth > 0 ? 0 : 6 }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>
                {d.depth > 0 ? '↳' : '🗂️'}
              </div>
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <div style={{ fontWeight: 600, color: 'var(--gray-900)', fontSize: 14.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</div>
                <div className="text-muted">{d.procedure_count || 0} {d.procedure_count === 1 ? 'procedura' : 'procedur'}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 16 }}>
        <button className="btn btn-secondary btn-sm" onClick={() => onSelect('__all__')}>Pokaż wszystkie procedury (wszystkie działy)</button>
      </div>
    </div>
  );
}

/* ── Quick Preview Panel ── */
function PreviewPanel({ proc, onClose, onOpen, onDownloadPdf, statusLabel, statusColor }) {
  const STATUS_BADGE = {};
  const STATUS_LABEL = statusLabel || {};
  const STATUS_COLOR = statusColor || {};
  if (!proc) return null;
  return (
    <div style={{
      position: 'fixed', top: 0, right: 0, bottom: 0, width: 420,
      background: 'white', boxShadow: '-4px 0 24px rgba(0,0,0,.13)',
      zIndex: 1000, display: 'flex', flexDirection: 'column',
      animation: 'slideInRight .2s ease'
    }}>
      <style>{`@keyframes slideInRight{from{transform:translateX(40px);opacity:0}to{transform:translateX(0);opacity:1}}`}</style>
      {/* Header */}
      <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid var(--gray-100)', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <span style={{ marginBottom: 8, display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
            background: (STATUS_COLOR[proc.status] || '#6b7280') + '22',
            color: STATUS_COLOR[proc.status] || '#6b7280' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: STATUS_COLOR[proc.status] || '#6b7280' }}/>
            {STATUS_LABEL[proc.status] || proc.status}
          </span>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--gray-900)', lineHeight: 1.35 }}>{proc.title}</h2>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', fontSize: 22, lineHeight: 1, padding: 4, flexShrink: 0 }}>×</button>
      </div>
      {/* Meta */}
      <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--gray-100)', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {proc.owner_name && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <span style={{ color: 'var(--gray-400)', width: 80 }}>Właściciel</span>
            <span style={{ color: 'var(--gray-800)', fontWeight: 500 }}>{proc.owner_name}</span>
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <span style={{ color: 'var(--gray-400)', width: 80 }}>Wersja</span>
          <span style={{ color: 'var(--gray-800)' }}>v{proc.current_version || 1}</span>
        </div>
        {proc.updated_at && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <span style={{ color: 'var(--gray-400)', width: 80 }}>Aktualizacja</span>
            <span style={{ color: 'var(--gray-800)' }}>{new Date(proc.updated_at).toLocaleDateString('pl-PL')}</span>
          </div>
        )}
        {(proc.departments || []).length > 0 && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13 }}>
            <span style={{ color: 'var(--gray-400)', width: 80, paddingTop: 2 }}>Działy</span>
            <div className="tags" style={{ flex: 1 }}>
              {proc.departments.map(d => <span key={d.id} className="tag">{d.name}</span>)}
            </div>
          </div>
        )}
      </div>
      {/* AI summary */}
      {proc.ai_summary && (
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--gray-100)' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 6 }}>Streszczenie AI</div>
          <div style={{ fontSize: 13.5, color: 'var(--gray-700)', lineHeight: 1.6 }}>🤖 {proc.ai_summary}</div>
        </div>
      )}
      {/* Attachments */}
      {(proc.attachments || []).length > 0 && (
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--gray-100)', flex: 1, overflowY: 'auto' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 8 }}>Pliki ({proc.attachments.length})</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {proc.attachments.map(a => (
              <a key={a.id} href={`/api/attachments/${a.id}/download`} target="_blank" rel="noreferrer"
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, background: 'var(--gray-50)', border: '1px solid var(--gray-100)', textDecoration: 'none', color: 'var(--gray-700)', fontSize: 13 }}
                onClick={e => e.stopPropagation()}>
                <span style={{ fontSize: 16 }}>📎</span>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.file_name}</span>
                <span style={{ fontSize: 11, color: 'var(--gray-400)', flexShrink: 0 }}>↓</span>
              </a>
            ))}
          </div>
        </div>
      )}
      {/* Actions */}
      <div style={{ padding: '16px 20px', borderTop: '1px solid var(--gray-100)', display: 'flex', gap: 8 }}>
        <button className="btn btn-primary" style={{ flex: 1 }} onClick={onOpen}>Otwórz procedurę</button>
        <button className="btn btn-secondary" style={{ flexShrink: 0 }} onClick={onDownloadPdf} title="Pobierz jako PDF">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16 }}>
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="12" y1="18" x2="12" y2="12"/>
            <line x1="9" y1="15" x2="15" y2="15"/>
          </svg>
          PDF
        </button>
      </div>
    </div>
  );
}

export default function ProceduresList() {
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  const initialDept = searchParams.get('dept') || '';
  const [view, setView] = useState((initialQuery || initialDept) ? 'list' : 'departments');
  const [activeDept, setActiveDept] = useState(initialQuery ? '__all__' : (initialDept || null));
  const [activeDeptName, setActiveDeptName] = useState(initialQuery ? `Wyniki dla "${initialQuery}"` : '');
  const [departments, setDepartments] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [search, setSearch] = useState(initialQuery);
  const [searchActive, setSearchActive] = useState(initialQuery);
  const [suggestions, setSuggestions] = useState([]);
  const [showSugg, setShowSugg] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null); // { proc, detail }
  const [previewLoading, setPreviewLoading] = useState(false);
  const { isAdmin, isDeptHead } = useAuth();
  const nav = useNavigate();
  const previewRef = useRef();
  const { labelMap: STATUS_LABEL, colorMap: STATUS_COLOR } = useStatuses();

  useEffect(() => {
    api.get('/departments').then(r => {
      setDepartments(r.data);
      setLoading(false);
      if (initialDept && !initialQuery) {
        const flat = flattenDepts(r.data);
        const match = flat.find(d => d.id === initialDept);
        if (match) setActiveDeptName(match.name);
      }
    }).catch(() => setLoading(false));
  }, []); // eslint-disable-line

  useEffect(() => {
    if (view === 'list') loadProcedures();
  }, [view, activeDept, statusFilter, searchActive]); // eslint-disable-line

  // Close preview on outside click
  useEffect(() => {
    function handleClick(e) {
      if (preview && previewRef.current && !previewRef.current.contains(e.target)) {
        const row = e.target.closest('[data-proc-row]');
        if (!row) setPreview(null);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [preview]);

  async function loadProcedures() {
    setLoading(true);
    try {
      const params = {};
      if (activeDept && activeDept !== '__all__') params.department_id = activeDept;
      if (statusFilter) params.status = statusFilter;
      if (searchActive) params.search = searchActive;
      const r = await api.get('/procedures', { params });
      setProcedures(r.data);
    } finally { setLoading(false); }
  }

  useEffect(() => {
    if (search.length >= 2) {
      api.get('/procedures/search-suggestions', { params: { q: search } }).then(r => { setSuggestions(r.data); setShowSugg(true); });
    } else { setSuggestions([]); setShowSugg(false); }
  }, [search]);

  function selectDepartment(deptId, deptName) {
    setActiveDept(deptId);
    setActiveDeptName(deptName || 'Wszystkie procedury');
    setView('list');
  }

  function handleSearch(e) {
    e.preventDefault();
    setShowSugg(false);
    setSearchActive(search);
    if (view !== 'list') setView('list');
  }

  function backToDepartments() {
    setView('departments');
    setActiveDept(null);
    setSearch('');
    setSearchActive('');
    setStatusFilter('');
    setPreview(null);
  }

  async function openPreview(proc, e) {
    e.stopPropagation();
    if (preview?.proc?.id === proc.id) { setPreview(null); return; }
    setPreview({ proc, detail: null });
    setPreviewLoading(true);
    try {
      const r = await api.get(`/procedures/${proc.id}`);
      setPreview({ proc, detail: r.data });
    } catch { /* show basic info */ }
    setPreviewLoading(false);
  }

  function handleDownloadPdf(proc) {
    // Open print-friendly view in new tab — browser handles PDF via Ctrl+P / Save as PDF
    window.open(`/procedures/${proc.id}?print=1`, '_blank');
  }

  function formatDate(s) { if (!s) return '—'; return new Date(s).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' }); }

  const previewProc = preview?.detail || preview?.proc;

  return (
    <div style={{ position: 'relative' }}>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {view === 'list' && (
            <button className="btn btn-secondary btn-sm" onClick={backToDepartments}>← Działy</button>
          )}
          <h1 className="page-title" style={{ margin: 0 }}>
            {view === 'departments' ? 'Wybierz dział' : activeDeptName}
          </h1>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {isAdmin && (
            <button className="btn btn-secondary" title="Pobierz ZIP ze wszystkimi procedurami"
              onClick={async () => {
                try {
                  const jwtToken = localStorage.getItem('token');
                  // Krok 1: pobierz jednorazowy token przez POST z nagłówkiem
                  const r = await fetch(`${BACKEND_URL}/api/export/download-token`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${jwtToken}` }
                  });
                  if (!r.ok) throw new Error('Błąd autoryzacji');
                  const { token } = await r.json();
                  // Krok 2: otwórz pobieranie przez window.location z krótkotrwałym tokenem
                  const date = new Date().toISOString().slice(0, 10);
                  window.location.href = `${BACKEND_URL}/api/export/procedures-zip?token=${token}`;
                } catch {
                  alert('Błąd pobierania ZIP');
                }
              }}>
              📦 Pobierz wszystkie (ZIP)
            </button>
          )}
          {(isAdmin || isDeptHead) && (
            <button className="btn btn-primary" onClick={() => nav('/procedures/new')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16 }}><path d="M12 5v14M5 12h14"/></svg>
              Nowa procedura
            </button>
          )}
        </div>
      </div>

      {/* Search bar */}
      <div className="card" style={{ marginBottom: 20, padding: '16px 20px' }}>
        <form onSubmit={handleSearch} style={{ position: 'relative' }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 20, height: 20, color: 'var(--gray-400)', flexShrink: 0 }}>
              <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
            </svg>
            <input
              className="form-input"
              style={{ border: 'none', fontSize: 15, padding: '4px 0' }}
              placeholder="Szukaj w tytułach i treści dokumentów Word..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              onBlur={() => setTimeout(() => setShowSugg(false), 200)}
            />
            {view === 'list' && (
              <select className="form-select" style={{ width: 180, flexShrink: 0 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                <option value="">Wszystkie statusy</option>
                {Object.entries(STATUS_LABEL).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            )}
            <button className="btn btn-primary btn-sm" type="submit">Szukaj</button>
          </div>
          {showSugg && suggestions.length > 0 && (
            <div className="search-suggestions" style={{ top: 'calc(100% + 8px)' }}>
              {suggestions.map(s => (
                <div key={s.id} className="search-suggestions-item" onMouseDown={() => { nav(`/procedures/${s.id}`); setShowSugg(false); }}>{s.title}</div>
              ))}
            </div>
          )}
        </form>
        <div className="text-muted" style={{ marginTop: 8 }}>🔍 Wyszukiwarka przeszukuje też zawartość wgranych plików Word</div>
      </div>

      {view === 'departments' ? (
        loading ? <div style={{ padding: 32, textAlign: 'center', color: 'var(--gray-400)' }}>Ładowanie...</div>
        : <DepartmentTiles departments={departments} onSelect={(id) => selectDepartment(id, id === '__all__' ? 'Wszystkie procedury' : null)} />
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--gray-400)' }}>Ładowanie...</div>
          ) : procedures.length === 0 ? (
            <div className="empty-state"><p>Brak procedur{searchActive ? ` dla zapytania "${searchActive}"` : ''}</p></div>
          ) : (
            <div>
              {/* Column headers */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 180px 140px 130px 70px 100px 80px', alignItems: 'center', padding: '10px 16px', borderBottom: '2px solid var(--gray-100)', background: 'var(--gray-50)' }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: .5 }}>Tytuł</div>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: .5 }}>Działy</div>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: .5 }}>Status</div>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: .5 }}>Właściciel</div>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: .5 }}>Wer.</div>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: .5 }}>Aktualizacja</div>
                <div></div>
              </div>
              <div>
                {procedures.map((p, i) => {
                  const isActive = preview?.proc?.id === p.id;
                  return (
                    <div key={p.id} data-proc-row="1"
                      style={{
                        display: 'grid', gridTemplateColumns: '1fr 180px 140px 130px 70px 100px 80px',
                        alignItems: 'center', padding: '12px 16px',
                        borderBottom: i < procedures.length - 1 ? '1px solid var(--gray-100)' : 'none',
                        cursor: 'pointer', transition: 'background .1s',
                        background: isActive ? 'var(--primary-light)' : 'white',
                      }}
                      onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'var(--gray-50)'; }}
                      onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'white'; }}
                      onClick={() => nav(`/procedures/${p.id}`)}>
                      {/* Title cell */}
                      <div style={{ paddingRight: 12, overflow: 'hidden' }}>
                        <div style={{ fontWeight: 600, color: 'var(--gray-900)', fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title}</div>
                        {p.ai_summary && (
                          <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            🤖 {p.ai_summary}
                          </div>
                        )}
                        {p.search_snippet && (
                          <div style={{ fontSize: 12, color: 'var(--gray-600)', marginTop: 4, background: '#fef9c3', padding: '3px 7px', borderRadius: 4, lineHeight: 1.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                            📄 {highlightSnippet(p.search_snippet, searchActive)}
                          </div>
                        )}
                      </div>
                      {/* Departments */}
                      <div>
                        <div className="tags">{(p.departments || []).slice(0, 2).map(d => <span key={d.id} className="tag">{d.name}</span>)}
                          {(p.departments || []).length > 2 && <span className="tag">+{p.departments.length - 2}</span>}
                        </div>
                      </div>
                      {/* Status */}
                      <div>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          padding: '3px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                          whiteSpace: 'nowrap',
                          background: STATUS_COLOR[p.status] + '18',
                          color: STATUS_COLOR[p.status]
                        }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0, background: STATUS_COLOR[p.status], display: 'inline-block' }}/>
                          {STATUS_LABEL[p.status]}
                        </span>
                      </div>
                      {/* Owner */}
                      <div style={{ fontSize: 13, color: 'var(--gray-600)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.owner_name || '—'}</div>
                      {/* Version */}
                      <div style={{ fontSize: 13, color: 'var(--gray-400)' }}>v{p.current_version || 1}</div>
                      {/* Date */}
                      <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>{formatDate(p.updated_at)}</div>
                      {/* Actions */}
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }} onClick={e => e.stopPropagation()}>
                        <button
                          title="Szybki podgląd"
                          onClick={e => openPreview(p, e)}
                          style={{
                            background: isActive ? 'var(--primary)' : 'var(--gray-100)',
                            color: isActive ? 'white' : 'var(--gray-500)',
                            border: 'none', borderRadius: 6, padding: '5px 8px', cursor: 'pointer', fontSize: 14
                          }}>
                          👁
                        </button>
                        <button
                          title="Pobierz PDF"
                          onClick={() => handleDownloadPdf(p)}
                          style={{ background: 'var(--gray-100)', color: 'var(--gray-500)', border: 'none', borderRadius: 6, padding: '5px 8px', cursor: 'pointer', fontSize: 14 }}>
                          📥
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quick Preview Panel */}
      {preview && (
        <div ref={previewRef}>
          <PreviewPanel
            proc={previewLoading ? preview.proc : previewProc}
            onClose={() => setPreview(null)}
            onOpen={() => nav(`/procedures/${preview.proc.id}`)}
            onDownloadPdf={() => handleDownloadPdf(preview.proc)}
            statusLabel={STATUS_LABEL}
            statusColor={STATUS_COLOR}
          />
          {/* Backdrop overlay on mobile */}
          <div style={{ position: 'fixed', inset: 0, zIndex: 999, pointerEvents: 'none' }} />
        </div>
      )}
    </div>
  );
}
